import {
  CheckCircleIcon,
  InfoIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useTracking } from '../../../react-components/tracking';
import { Button } from '../../interaction/Buttons/Default Button/Button';
import { IconLibrary } from '../IconLibrary/IconLibrary';
import { IconName } from '../IconLibrary/IconLibraryTypes';
import { alertColors, textColors } from '../../theme/colors/alert-colors';

import './Alert.scss';

type AlertSeverity = 'error' | 'warning' | 'info' | 'success';
type AlertVariant = 'tonal' | 'outlined' | 'filled';

export interface AlertProps {
  /**
   * The severity/type of the alert
   * @default 'error'
   */
  severity?: AlertSeverity;
  /**
   * The visual variant of the alert
   * @default 'tonal'
   */
  variant?: AlertVariant;
  /**
   * Optional title for the alert
   */
  title?: string;
  /**
   * The main alert message content
   */
  children: React.ReactNode;
  /**
   * Optional action button configuration
   */
  action?: {
    label: string;
    onClick: () => void;
    /**
     * Whether to debounce the action button to prevent rapid clicks
     * @default false
     */
    debounced?: boolean;
  };
  /**
   * Whether to show the close button
   * @default false
   */
  closable?: boolean;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * Optional custom icon name from IconLibrary
   * If provided, overrides the default severity-based icon
   */
  iconName?: IconName;
  /**
   * Optional tracking name for DataDog tracking
   */
  trackingName?: string;
  /**
   * Additional CSS class name
   */
  className?: string;
}

const getIconForSeverity = (severity: AlertSeverity) => {
  const iconProps = { size: 24, weight: 'fill' as const };

  switch (severity) {
    case 'error':
      return <WarningCircleIcon {...iconProps} />;
    case 'warning':
      return <WarningIcon {...iconProps} />;
    case 'info':
      return <InfoIcon {...iconProps} />;
    case 'success':
      return <CheckCircleIcon {...iconProps} />;
    default:
      return <InfoIcon {...iconProps} />;
  }
};

const getIconColor = (
  severity: AlertSeverity,
  variant: AlertVariant
): string => {
  if (variant === 'filled') {
    return textColors.inverse;
  }

  return alertColors[severity].primary;
};

/**
 * Alert component displays important messages to users.
 * Based on the Gneiss Design System.
 *
 * @example
 * ```tsx
 * <Alert severity="error" variant="tonal">
 *   This is an error message
 * </Alert>
 *
 * <Alert
 *   severity="success"
 *   variant="outlined"
 *   title="Success"
 *   action={{ label: 'Undo', onClick: handleUndo }}
 *   closable
 *   onClose={handleClose}
 * >
 *   Your changes have been saved
 * </Alert>
 *
 * <Alert
 *   severity="info"
 *   variant="tonal"
 *   iconName="Clock"
 * >
 *   Custom icon from IconLibrary
 * </Alert>
 *
 * <Alert
 *   severity="warning"
 *   variant="tonal"
 *   action={{ label: 'Extend time', onClick: handleExtend, debounced: true }}
 * >
 *   Action with debounce protection
 * </Alert>
 * ```
 */
export const Alert = ({
  severity = 'error',
  variant = 'tonal',
  title,
  children,
  action,
  closable = false,
  onClose,
  iconName,
  trackingName,
  className,
}: AlertProps) => {
  const { generateTrackingProps } = useTracking();
  const hasTitle = Boolean(title);
  const hasActions = action || closable;

  const iconColor = getIconColor(severity, variant);
  const defaultIcon = getIconForSeverity(severity);

  const containerClasses = [
    'alert-container',
    `alert-container--${severity}`,
    `alert-container--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const contentAreaClasses = [
    'alert-content-area',
    hasTitle && 'alert-content-area--has-title',
  ]
    .filter(Boolean)
    .join(' ');

  const leadingIconClasses = [
    'alert-leading-icon',
    hasTitle && 'alert-leading-icon--has-title',
  ]
    .filter(Boolean)
    .join(' ');

  const textContainerClasses = [
    'alert-text-container',
    hasTitle && 'alert-text-container--has-title',
  ]
    .filter(Boolean)
    .join(' ');

  const titleClasses = ['alert-title', `alert-title--${variant}`]
    .filter(Boolean)
    .join(' ');

  const messageClasses = ['alert-message', `alert-message--${variant}`]
    .filter(Boolean)
    .join(' ');

  const actionButtonClasses = [
    'alert-action-button',
    `alert-action-button--${variant}`,
  ]
    .filter(Boolean)
    .join(' ');

  const closeButtonClasses = [
    'alert-close-button',
    `alert-close-button--${variant}`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="alert"
      className={containerClasses}
      {...generateTrackingProps(trackingName || 'alert')}
    >
      <div className="alert-content">
        <div className={contentAreaClasses}>
          <div className={leadingIconClasses}>
            <div className="alert-icon" style={{ color: iconColor }}>
              {iconName ? (
                <IconLibrary
                  iconName={iconName}
                  size={24}
                  weight="fill"
                  color={iconColor}
                />
              ) : (
                defaultIcon
              )}
            </div>
          </div>

          <div className={textContainerClasses}>
            {title && <p className={titleClasses}>{title}</p>}
            <p className={messageClasses}>{children}</p>
          </div>
        </div>

        {hasActions && (
          <div className="alert-actions-container">
            {action && (
              <Button
                variant="text"
                className={actionButtonClasses}
                onClick={action.onClick}
                debounced={action.debounced}
                trackingName={
                  trackingName ? `${trackingName}-action` : 'alert-action'
                }
              >
                {action.label}
              </Button>
            )}
            {closable && onClose && (
              <button
                type="button"
                className={closeButtonClasses}
                onClick={onClose}
                aria-label="Close alert"
                {...generateTrackingProps(
                  trackingName ? `${trackingName}-close` : 'alert-close'
                )}
              >
                <XIcon size={24} weight="bold" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
